import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import PremiumModal from './PremiumModal';

const Backdrop = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  color: #333;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h2`
  font-size: 2rem;
  margin-bottom: 30px;
  text-align: center;
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SegmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
`;

const SegmentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 10px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e8e8e8;
  }
`;

const ColorPreview = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${props => props.color};
  border: 2px solid #fff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
`;

const Input = styled.input`
  flex: 1;
  padding: 10px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #4ecdc4;
  }
`;

const ColorInput = styled.input`
  width: 50px;
  height: 35px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
`;

const DeleteButton = styled.button`
  padding: 8px 12px;
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #ff5252;
    transform: translateY(-1px);
  }
`;

const AddButton = styled.button`
  width: 100%;
  padding: 15px;
  background: #4ecdc4;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 20px;
  
  &:hover {
    background: #45b7b8;
    transform: translateY(-2px);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 12px 30px;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &.primary {
    background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
    color: white;
  }
  
  &.secondary {
    background: #e0e0e0;
    color: #333;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
`;

const PresetButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
`;

const PresetButton = styled.button`
  padding: 8px 15px;
  background: rgba(78, 205, 196, 0.2);
  color: #333;
  border: 1px solid #4ecdc4;
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(78, 205, 196, 0.4);
    transform: translateY(-1px);
  }
`;

// Preset colors for suggestions
const PRESET_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#F7DC6F', '#BB8FCE',
  '#85C1E2', '#F8B739', '#52D726', '#FF0080', '#7E57C2'
];

// Preset segments
const PRESET_SEGMENTS = {
  classic: [
    { text: 'Take 2 shots', color: '#FF6B6B' },
    { text: 'Give a drink', color: '#4ECDC4' },
    { text: 'Truth or Dare', color: '#45B7D1' },
    { text: 'Skip your turn', color: '#F7DC6F' },
    { text: 'Everyone drinks', color: '#BB8FCE' },
    { text: 'Make a rule', color: '#85C1E2' }
  ],
  hardcore: [
    { text: 'Take 3 shots', color: '#FF0080' },
    { text: 'Finish your drink', color: '#FF6B6B' },
    { text: 'Waterfall', color: '#4ECDC4' },
    { text: 'Never have I ever', color: '#45B7D1' },
    { text: 'Most likely to...', color: '#F7DC6F' },
    { text: 'Shots for everyone', color: '#BB8FCE' }
  ],
  fun: [
    { text: 'Dance for 30s', color: '#F8B739' },
    { text: 'Sing a song', color: '#52D726' },
    { text: 'Tell a joke', color: '#FF0080' },
    { text: 'Compliment everyone', color: '#7E57C2' },
    { text: 'Do 10 pushups', color: '#4ECDC4' },
    { text: 'Speak in accent', color: '#FF6B6B' }
  ]
};

const WheelConfig = ({ segments, onSave, onClose }) => {
  const { user, isPremium, getFeatureLimit } = useAuth();
  const [editedSegments, setEditedSegments] = useState(segments);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [maxSegments, setMaxSegments] = useState(6);

  // Load feature limits on component mount
  useEffect(() => {
    const loadFeatureLimits = async () => {
      if (user && getFeatureLimit) {
        try {
          const limit = await getFeatureLimit('wheel_segments');
          setMaxSegments(limit);
        } catch (error) {
          console.error('Error loading feature limits:', error);
        }
      }
    };
    
    loadFeatureLimits();
  }, [user, getFeatureLimit]);

  const handleSegmentChange = (index, field, value) => {
    const updated = [...editedSegments];
    updated[index] = { ...updated[index], [field]: value };
    setEditedSegments(updated);
  };

  const addSegment = () => {
    // Check if user can add more segments
    if (editedSegments.length >= maxSegments && maxSegments !== -1) {
      setShowPremiumModal(true);
      return;
    }
    
    const newColor = PRESET_COLORS[editedSegments.length % PRESET_COLORS.length];
    setEditedSegments([...editedSegments, { text: 'New segment', color: newColor }]);
  };

  const deleteSegment = (index) => {
    if (editedSegments.length > 2) { // Minimum 2 segments
      setEditedSegments(editedSegments.filter((_, i) => i !== index));
    }
  };

  const applyPreset = (presetName) => {
    const preset = PRESET_SEGMENTS[presetName];
    // Check if preset exceeds user's limit
    if (preset.length > maxSegments && maxSegments !== -1) {
      setShowPremiumModal(true);
      return;
    }
    setEditedSegments(preset);
  };

  const handleSave = () => {
    onSave(editedSegments);
  };

  return (
    <AnimatePresence>
      <Backdrop
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Modal
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Title>Configure Wheel Segments 🎯</Title>
          
          <PresetButtons>
            <PresetButton onClick={() => applyPreset('classic')}>
              Classic Mode
            </PresetButton>
            <PresetButton onClick={() => applyPreset('hardcore')}>
              Hardcore Mode
            </PresetButton>
            <PresetButton onClick={() => applyPreset('fun')}>
              Fun Mode
            </PresetButton>
          </PresetButtons>
          
          <SegmentList>
            {editedSegments.map((segment, index) => (
              <SegmentItem key={index}>
                <ColorPreview color={segment.color} />
                <Input
                  type="text"
                  value={segment.text}
                  onChange={(e) => handleSegmentChange(index, 'text', e.target.value)}
                  placeholder="Segment text"
                />
                <ColorInput
                  type="color"
                  value={segment.color}
                  onChange={(e) => handleSegmentChange(index, 'color', e.target.value)}
                />
                <DeleteButton
                  onClick={() => deleteSegment(index)}
                  disabled={editedSegments.length <= 2}
                >
                  Delete
                </DeleteButton>
              </SegmentItem>
            ))}
          </SegmentList>
          
          <AddButton 
            onClick={addSegment}
            disabled={editedSegments.length >= maxSegments && maxSegments !== -1}
            style={{
              opacity: editedSegments.length >= maxSegments && maxSegments !== -1 ? 0.6 : 1,
              cursor: editedSegments.length >= maxSegments && maxSegments !== -1 ? 'not-allowed' : 'pointer'
            }}
          >
            + Add New Segment
            {maxSegments !== -1 && (
              <span style={{ fontSize: '0.8em', marginLeft: '10px' }}>
                ({editedSegments.length}/{maxSegments})
              </span>
            )}
          </AddButton>
          
          {!isPremium && maxSegments !== -1 && (
            <div style={{
              background: 'linear-gradient(45deg, #4ecdc4, #45b7d1)',
              color: 'white',
              padding: '15px',
              borderRadius: '10px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                ✨ Want unlimited segments?
              </div>
              <div style={{ fontSize: '0.9em', opacity: 0.9 }}>
                Upgrade to Premium for unlimited wheel segments and more features!
              </div>
            </div>
          )}
          
          <ButtonGroup>
            <Button className="secondary" onClick={onClose}>
              Cancel
            </Button>
            {!isPremium && (
              <Button 
                className="secondary" 
                onClick={() => setShowPremiumModal(true)}
                style={{
                  background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4)',
                  color: 'white',
                  opacity: 0.8
                }}
              >
                💾 Save Wheel (Premium)
              </Button>
            )}
            {isPremium && (
              <Button 
                className="secondary"
                onClick={() => {
                  // TODO: Implement actual wheel saving to database
                  alert('Wheel saved! (Feature coming soon)');
                }}
                style={{
                  background: '#4ecdc4',
                  color: 'white'
                }}
              >
                💾 Save Wheel
              </Button>
            )}
            <Button className="primary" onClick={handleSave}>
              Apply Changes
            </Button>
          </ButtonGroup>
        </Modal>
      </Backdrop>
      
      {/* Premium Modal */}
      <PremiumModal 
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        feature="wheel_segments"
      />
    </AnimatePresence>
  );
};

export default WheelConfig; 